import { Box, ChakraProps } from "@chakra-ui/react";
import { BullSilhouette } from "./BullSilhouette";
import { ShieldGlyph } from "./ShieldGlyph";
import { PartnerWatermark } from "../../constants/partners";

interface PartnerBackgroundProps {
  /** Partner accent (hex) that drives the glow + watermark. */
  accent: string;
  /** Same accent as "r, g, b" for translucent glows. */
  accentRgb: string;
  /** Which glyph is watermarked behind the hero. */
  watermark: PartnerWatermark;
}

// Per-glyph placement. The artworks have very different aspect ratios (the bull
// is wide, the shield is tall), so each gets its own footprint rather than
// sharing one that only flatters the bull.
const WATERMARKS: Record<
  PartnerWatermark,
  { Glyph: typeof BullSilhouette; layout: ChakraProps }
> = {
  bull: {
    Glyph: BullSilhouette,
    layout: {
      top: { base: "2%", md: "-2%" },
      width: "min(760px, 96vw)",
      opacity: 0.22,
    },
  },
  shield: {
    Glyph: ShieldGlyph,
    layout: {
      top: { base: "1%", md: "-1%" },
      width: "min(420px, 58vw)",
      opacity: 0.2,
    },
  },
};

// Full-viewport atmospheric backdrop for partner Drip pages, in the vibe of the
// bullprint.xyz "$ANSEM terminal": a near-black base, one soft accent glow behind
// the hero with a violet counter-glow (Solana pairing), a faint hairline "quant"
// grid that fades out below the hero, and the partner glyph watermark.
//
// Sits behind all page content (zIndex -1, pointer-events none) and is scoped to
// the route that renders it, so /drip and other pages are untouched.
export const PartnerBackground = ({
  accent,
  accentRgb,
  watermark,
}: PartnerBackgroundProps) => {
  const { Glyph, layout } = WATERMARKS[watermark];

  return (
    <Box
      aria-hidden
      position="fixed"
      inset={0}
      zIndex={-1}
      pointerEvents="none"
      overflow="hidden"
      background={`
        radial-gradient(ellipse 70% 55% at 50% 8%, rgba(${accentRgb}, 0.13), transparent 60%),
        radial-gradient(900px 620px at 82% -4%, rgba(153, 69, 255, 0.10), transparent 62%),
        #0a0a0c
      `}
    >
      {/* Hairline quant grid, concentrated near the top and faded out below. */}
      <Box
        position="absolute"
        inset={0}
        backgroundImage={`
          linear-gradient(rgba(255, 255, 255, 0.035) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px)
        `}
        backgroundSize="64px 64px"
        sx={{
          maskImage:
            "radial-gradient(ellipse 85% 55% at 50% 12%, #000 0%, transparent 72%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 85% 55% at 50% 12%, #000 0%, transparent 72%)",
        }}
      />

      {/* Partner glyph watermark behind the hero. */}
      <Glyph
        accent={accent}
        position="absolute"
        left="50%"
        transform="translateX(-50%)"
        height="auto"
        {...layout}
      />
    </Box>
  );
};
